/**
 * English strings for the owner screens.
 *
 * Written out in full and derived from nothing — see en/index.ts. Typed as
 * a complete slice, so a key missing here is a build error rather than a
 * screen that quietly renders Bosnian.
 *
 * The audience differs from the rest of the application. Everywhere else,
 * English is for a visitor on the floor who does not read the local
 * language; here it is for whoever is running the platform, so the words can
 * be the ones the business actually uses — adoption, churn, dormant — and
 * the uncomfortable sentences stay uncomfortable. "Possibly a guessed
 * password" is what an owner needs to read over that alert, and is not
 * softened into "unusual sign-in activity".
 *
 * "Report" throughout for *prijava*, matching en/auth.ts. A worker files a
 * report; nothing here is a "submission" except in the API.
 */

import type { OwnerSlice } from '../bs/owner'

export const owner: OwnerSlice = {
  // ---- Shared vocabulary --------------------------------------------
  'owner.panelName': 'Owner console',
  'owner.value.never': 'never',
  'owner.action.cancel': 'Cancel',
  'owner.action.save': 'Save',
  'owner.form.fullName': 'Full name',
  'owner.form.employeeCode': 'Employee code',
  'owner.form.email': 'E-mail',
  'owner.action.back': 'Back',

  /*
   * Two forms in three slots, which is the correct English answer rather
   * than a gap: `few` and `many` both take the plural because English has
   * nothing between 2 and 5 to distinguish.
   */
  'owner.count.users.one': '{count} user',
  'owner.count.users.few': '{count} users',
  'owner.count.users.many': '{count} users',
  'owner.count.centres.one': '{count} work centre',
  'owner.count.centres.few': '{count} work centres',
  'owner.count.centres.many': '{count} work centres',
  'owner.count.companies.one': '{count} company',
  'owner.count.companies.few': '{count} companies',
  'owner.count.companies.many': '{count} companies',
  'owner.count.suspended.one': '{count} suspended',
  'owner.count.suspended.few': '{count} suspended',
  'owner.count.suspended.many': '{count} suspended',
  'owner.count.members.one': '{count} team member',
  'owner.count.members.few': '{count} team members',
  'owner.count.members.many': '{count} team members',

  // ---- The console shell --------------------------------------------
  'owner.nav.overview': 'Overview',
  'owner.nav.overview.hint': 'What is happening across the platform',
  'owner.nav.companies': 'Companies',
  'owner.nav.companies.hint': 'Customers and their settings',
  'owner.nav.analytics': 'Analytics',
  'owner.nav.analytics.hint': 'Adoption, and customers going quiet',
  'owner.nav.team': 'Team',
  'owner.nav.team.hint': 'Who runs the platform, and with what powers',
  'owner.nav.system': 'System',
  'owner.nav.system.hint': 'Queue, scheduled tasks, disk, sign-ins',
  'owner.nav.aria': 'Console navigation',

  'owner.account.aria': 'Account — {name}',
  'owner.account.myAccount': 'My account',
  'owner.account.signOut': 'Sign out',

  'owner.footer.copyright': 'KaProLean · owner console · © {year}',
  'owner.footer.software': 'Software:',

  // ---- Dashboard ----------------------------------------------------
  'owner.dashboard.loading': 'Loading the overview…',
  'owner.dashboard.title': 'Platform overview',
  'owner.dashboard.subtitle': 'Every company together. Refreshed every minute.',

  // ---- Companies ----------------------------------------------------
  'owner.companies.title': 'Companies',
  'owner.companies.subtitle': 'The customers on this installation.',
  'owner.companies.new': 'New company',
  'owner.companies.search.placeholder': 'Company name or code',
  'owner.companies.search.aria': 'Search companies',
  'owner.companies.loading': 'Loading companies…',
  'owner.companies.empty.title': 'No companies',
  'owner.companies.empty.description': 'No company matches that search.',
  'owner.companies.empty.hint': 'Create the first one with the “New company” button.',
  'owner.companies.impersonate.description': 'Choose whose screen you want to see.',

  'owner.companies.saved.created': 'The company has been created.',
  'owner.companies.saved.updated': 'The company has been changed.',

  'owner.companies.form.description': 'The customer’s details and their settings.',
  'owner.companies.form.code': 'Code',
  'owner.companies.form.code.hint':
    'Capitals and digits. It appears in reports and in file names.',
  'owner.companies.form.name': 'Name',
  'owner.companies.form.contactEmail': 'Contact e-mail',
  'owner.companies.form.contactPhone': 'Contact phone',
  'owner.companies.form.logo': 'Logo (URL)',
  'owner.companies.form.logo.hint':
    'The address of an image the customer hosts themselves. It appears in the sidebar of their app.',
  'owner.companies.form.logo.placeholder': 'https://company.example/logo.svg',
  'owner.companies.form.logoDark': 'Logo for the dark theme (URL)',
  'owner.companies.form.logoDark.hint':
    'Optional. A logo with dark lettering disappears on the dark theme — without this variant we show it on a white panel.',
  'owner.companies.form.logoDark.placeholder': 'https://company.example/logo-white.svg',
  'owner.companies.form.currency': 'Currency',
  'owner.companies.form.currency.hint':
    'What this company counts savings, costs and awards in. Set here when the company is opened; their own administrator can change it afterwards.',
  'owner.companies.form.maxUpload': 'Largest file (MB)',
  'owner.companies.form.maxUpload.hint':
    'Applies to a single video or photo attached to a report.',

  'owner.companies.form.admin.legend': 'Company administrator',
  'owner.companies.form.admin.note':
    'We send an invitation to this address with a link where they set their own password. You do not type it and nobody sees it.',
  'owner.companies.form.admin.employeeCode.hint':
    'This is what they sign in with — not the e-mail.',

  // ---- Impersonation ------------------------------------------------
  'owner.impersonate.action': 'Take over',
  'owner.impersonate.started': 'You are now signed in as {name}.',
  'owner.impersonate.notice':
    'The session lasts one hour and is visible to the user. Everything you do is recorded in the company’s log under your name.',
  'owner.impersonate.search.placeholder': 'Worker name or code',
  'owner.impersonate.empty.title': 'No users',
  'owner.impersonate.empty.description': 'No account matches that search.',

  // ---- Analytics ----------------------------------------------------
  'owner.analytics.loading': 'Loading the analytics…',
  'owner.analytics.title': 'Platform analytics',
  'owner.analytics.subtitle': 'How much of what we sold is actually being used.',
  'owner.analytics.period.aria': 'Period',
  'owner.analytics.period.one': 'The last {count} month',
  'owner.analytics.period.few': 'The last {count} months',
  'owner.analytics.period.many': 'The last {count} months',

  'owner.analytics.monthly.title': 'Activity by month',
  'owner.analytics.monthly.description':
    'Reports, 5S evaluations and new accounts, every company together.',

  'owner.analytics.types.title': 'Kinds of report',
  'owner.analytics.types.description': 'What people actually report.',

  'owner.analytics.contributors.title': 'Who is carrying a customer',
  'owner.analytics.contributors.description': 'Most reports per person.',
  'owner.analytics.contributors.empty': 'Not a single report yet.',

  'owner.analytics.bars.submissions': 'Reports',
  'owner.analytics.bars.evaluations': '5S',
  'owner.analytics.bars.newUsers': 'New accounts',
  'owner.analytics.bars.tooltip': '{label}: {count}',

  // ---- System health ------------------------------------------------
  'owner.health.loading': 'Checking the system…',
  'owner.health.title': 'System',
  'owner.health.checkedAt': 'Checked {when}. Refreshed every 30 seconds.',
  'owner.health.notMeasurable': 'cannot be checked',
  'owner.health.minutes': '{count} min',

  'owner.health.queue.title': 'Queue',
  'owner.health.queue.description':
    'E-mail and live notifications are processed here. If the worker stops, the app carries on working — it just quietly stops sending.',
  'owner.health.queue.pending': 'Waiting',
  'owner.health.queue.oldest': 'Oldest job waiting',
  'owner.health.queue.oldest.warning':
    'A job has been waiting more than ten minutes — check that kapro-queue is running.',
  'owner.health.queue.failed': 'Failed',
  'owner.health.queue.lastFailure': 'Last failure {when}.',
  'owner.health.queue.depth': 'Queue depth',

  'owner.health.scheduler.title': 'Schedule',
  'owner.health.scheduler.description': 'The daily reminders and the monthly 5S check.',
  'owner.health.scheduler.lastNotification': 'Last notification',

  'owner.health.storage.title': 'Space',
  'owner.health.storage.description': 'Disk “{disk}” and the size of the attachments.',
  'owner.health.storage.free': 'Free',
  'owner.health.storage.total': 'of {total} in total',
  'owner.health.storage.notLocal': 'The disk path is not a local one.',
  'owner.health.storage.attachments': 'Attachments',

  'owner.health.database.title': 'Database',
  'owner.health.database.description': 'Connection “{connection}”.',
  'owner.health.database.latency': 'Round trip',
  'owner.health.database.ms': '{count} ms',
  'owner.health.database.pendingMigrations': 'Migrations not run',
  'owner.health.database.pendingMigrations.warning':
    'The deploy was interrupted, or the migrations were never run. Run php artisan migrate.',

  // ---- Sparkline ----------------------------------------------------
  'owner.sparkline.aria.one':
    '{count} report in the last {days} days, peaking at {peak} in one day.',
  'owner.sparkline.aria.few':
    '{count} reports in the last {days} days, peaking at {peak} in one day.',
  'owner.sparkline.aria.many':
    '{count} reports in the last {days} days, peaking at {peak} in one day.',
  'owner.sparkline.caption': '{total} in total · peak {peak}',

  // The MES integration screen. See pages/owner/CompanyMes.tsx for why the
  // secret fields explain what blank means.
  'owner.mes.field.baseUrl': 'MES address',
  'owner.mes.field.baseUrl.hint': 'The base address, no path. E.g. https://mes.company.com',
  'owner.mes.field.token': 'API token',
  'owner.mes.field.token.hint': 'A Sanctum token issued to you by the MES.',
  'owner.mes.field.token.stored': 'A token is stored ({hint}). Leave blank to keep it, type "null" to clear it.',
  'owner.mes.secret.unchanged': 'unchanged',
  'owner.mes.field.plantIds': 'Plant ids',
  'owner.mes.field.plantIds.hint': 'The id_pogon values from the MES, comma separated. The test below lists them.',
  'owner.mes.webhooks.title': 'Events to the MES',
  'owner.mes.webhooks.note': 'This is where we send submissions and evaluations. The MES needs an endpoint that accepts them and verifies the signature.',
  'owner.mes.field.webhookUrl': 'Event endpoint',
  'owner.mes.field.webhookSecret': 'Shared secret',
  'owner.mes.field.webhookSecret.hint': 'We sign every request with it so the MES can prove it came from us.',
  'owner.mes.events.title': 'Which events to send',
  'owner.mes.events.all': 'Currently sending every event.',
  'owner.mes.events.chosen': '{count} selected.',
  'owner.mes.action.test': 'Test connection',
  'owner.mes.action.sync': 'Sync now',
  'owner.mes.synced.counts': 'Imported: {plants} plants, {centres} work centres, {users} workers.',
  'owner.mes.plants.title': 'Plants in the MES',
  'owner.mes.deliveries.title': 'Recent deliveries',
  'owner.mes.deliveries.attempts': '{count} attempts',
  'owner.mes.failing.title': 'Deliveries are failing',
  'owner.mes.failing.body': '{count} deliveries failed. The MES did not receive them.',
  // ---- Roles on the vendor's own team ---------------------------------
  'owner.role.full': 'Full access',
  'owner.role.full.description': 'Everything: opens and configures companies, suspends them, manages the team.',
  'owner.role.support': 'Support',
  'owner.role.support.description':
    'Sees everything, signs in as users, ends sessions and tests the MES. Does not change company settings.',
  'owner.role.viewer': 'View only',
  'owner.role.viewer.description': 'Sees everything, changes nothing and never signs in as anyone.',

  'owner.locked': 'Your role ({role}) does not allow this. Allowed for: {roles}.',
  'owner.readOnly': 'Read only — your role ({role}) cannot change this. Allowed for: {roles}.',

  // ---- Status and usage -------------------------------------------------
  'owner.status.active': 'Active',
  'owner.status.suspended': 'Suspended',
  'owner.usage.of': '{used} / {limit}',
  'owner.usage.ofUnlimited': '{used} · no limit',

  // ---- Company list -----------------------------------------------------
  'owner.companies.summary': '{total} · {suspended}',
  'owner.companies.filter.aria': 'Company status',
  'owner.companies.filter.all': 'All',
  'owner.companies.filter.active': 'Active',
  'owner.companies.filter.suspended': 'Suspended',
  'owner.companies.col.company': 'Company',
  'owner.companies.col.seats': 'Seats',
  'owner.companies.col.activity': 'Last activity',
  'owner.companies.create': 'Open the company',

  // ---- One customer's workspace ----------------------------------------
  'owner.workspace.loading': 'Loading the company…',
  'owner.workspace.back': 'All companies',
  'owner.workspace.notFound.title': 'Company not found',
  'owner.workspace.notFound.description': 'The link may be old, or the address was copied wrongly.',
  'owner.workspace.customerSince': 'customer since {date}',
  'owner.workspace.impersonate': 'Sign in as…',
  'owner.workspace.tab.overview': 'Overview',
  'owner.workspace.tab.analytics': 'Analytics',
  'owner.workspace.tab.people': 'People',
  'owner.workspace.tab.access': 'Access',
  'owner.workspace.tab.sessions': 'Sessions',
  'owner.workspace.tab.activity': 'Log',
  'owner.workspace.tab.integration': 'Integration',
  'owner.workspace.tab.settings': 'Settings',

  'owner.suspend.action': 'Suspend',
  'owner.suspend.title': 'Suspend access for {name}?',
  'owner.suspend.body':
    'Everyone at this company is signed out at once and cannot sign back in — not with a password, a badge or an activation code — until you reactivate the company. Their data stays untouched.',
  'owner.suspend.reason': 'Reason (optional)',
  'owner.suspend.reason.hint': 'Shown word for word on the sign-in screen to everyone at the company.',
  'owner.suspend.reason.placeholder': 'e.g. At the request of the company’s management, until further notice.',
  'owner.suspend.confirm': 'Suspend access',
  'owner.suspend.sessionsEnded.one': '{count} session was ended.',
  'owner.suspend.sessionsEnded.few': '{count} sessions were ended.',
  'owner.suspend.sessionsEnded.many': '{count} sessions were ended.',
  'owner.reactivate.action': 'Reactivate',
  'owner.reactivate.title': 'Reactivate {name}?',
  'owner.reactivate.body':
    'Their people can sign in again. Sessions ended by the suspension do not come back — everyone signs in afresh.',
  'owner.reactivate.confirm': 'Reactivate',
  'owner.suspension.title': 'Access is suspended',
  'owner.suspension.since': 'Access suspended since {date}',
  'owner.suspension.reason': 'People see this when they sign in: “{reason}”',
  'owner.suspension.noReason': 'No reason given — people only see that access is suspended.',

  // ---- Workspace: overview ----------------------------------------------
  'owner.overview.loading': 'Loading the overview…',
  'owner.overview.seats': 'Seats',
  'owner.overview.seats.hint': 'Active users who are not owners, against the limit on the Access tab.',
  'owner.overview.storage': 'Storage',
  'owner.overview.storage.hint': 'The combined size of every attachment this company holds.',
  'owner.overview.online': 'In the app now',
  'owner.overview.online.hint': 'Used it in the last five minutes.',
  'owner.overview.mes': 'KaproMES',
  'owner.overview.mes.notConfigured': 'Not set up',
  'owner.overview.mes.moduleOff': 'Set up, module off',
  'owner.overview.mes.failing.one': '{count} failed delivery',
  'owner.overview.mes.failing.few': '{count} failed deliveries',
  'owner.overview.mes.failing.many': '{count} failed deliveries',
  'owner.overview.mes.ok': 'Working',
  'owner.overview.mes.lastSync': 'Last sync {when}',
  'owner.overview.mes.neverSynced': 'Never synced yet',
  'owner.overview.people.title': 'People',
  'owner.overview.people.description': 'From accounts handed out to accounts actually used.',
  'owner.overview.people.total': 'Accounts',
  'owner.overview.people.active': 'Active',
  'owner.overview.people.last30': 'Signed in, 30 days',
  'owner.overview.people.last7': 'Signed in, 7 days',
  'owner.overview.people.never': 'Never signed in: {count}',
  'owner.overview.people.pendingPassword': 'Still on an activation code: {count}',
  'owner.overview.people.admins': 'Administrators: {count}',
  'owner.overview.activity.title': 'Activity',
  'owner.overview.activity.description': 'When each thing was last done.',
  'owner.overview.activity.lastSignIn': 'Last sign-in',
  'owner.overview.activity.lastSubmission': 'Last report',
  'owner.overview.activity.lastEvaluation': 'Last 5S evaluation',
  'owner.overview.activity.moduleOff': 'module off',
  'owner.overview.activity.submissions30': 'Reports in 30 days: {count} · open tasks: {tasks}',
  'owner.overview.activity.evaluations30': 'Evaluations in 30 days: {count}',

  // ---- Workspace: people ------------------------------------------------
  'owner.people.description': 'Signing in as someone and ending sessions are both recorded in the company’s log.',
  'owner.people.search.aria': 'Search users',
  'owner.people.inactive': 'inactive',
  'owner.people.lastLogin': 'last seen {when}',
  'owner.people.neverLoggedIn': 'never signed in',
  'owner.people.endSessions.action': 'Sign out',
  'owner.people.endSessions.aria': 'Sign {name} out everywhere',
  'owner.people.endSessions.title': 'Sign {name} out on every device?',
  'owner.people.endSessions.body':
    'Every open session of this user ends at once, on every device. The account stays active and can sign in again — enough for a lost phone; for somebody who has left, the company’s administrator deactivates the account.',
  'owner.people.endSessions.confirm': 'Sign out everywhere',

  // ---- Impersonation reason ---------------------------------------------
  'owner.impersonate.reason': 'Reason',
  'owner.impersonate.reason.hint':
    '{count} / {max} characters, at least {min}. Written to the company’s log, where their administrator can read it.',
  'owner.impersonate.reason.placeholder': 'e.g. Cannot see their task since this morning — on a call with the administrator.',
  'owner.impersonate.confirm': 'Sign in as {name}',

  // ---- Workspace: sessions ----------------------------------------------
  'owner.sessions.loading': 'Loading sessions…',
  'owner.sessions.count.one': '{count} open session',
  'owner.sessions.count.few': '{count} open sessions',
  'owner.sessions.count.many': '{count} open sessions',
  'owner.sessions.summary': 'In the app now: {online} · different people: {people}',
  'owner.sessions.online': 'In the app now',
  'owner.sessions.offline': 'Not used in the last five minutes',
  'owner.sessions.col.person': 'User',
  'owner.sessions.col.device': 'Device',
  'owner.sessions.col.device.explain':
    'Read from the browser’s details, so it is approximate. Hover for the exact string; below it is the name the device gave itself at sign-in.',
  'owner.sessions.col.ip': 'IP address',
  'owner.sessions.col.lastUsed': 'Last used',
  'owner.sessions.col.actions': 'Actions',
  'owner.sessions.device.unknown': 'Unknown device',
  'owner.sessions.kind.phone': 'phone',
  'owner.sessions.kind.tablet': 'tablet',
  'owner.sessions.kind.desktop': 'computer',
  'owner.sessions.kind.unknown': 'unknown kind of device',
  'owner.sessions.impersonation': 'Support',
  'owner.sessions.neverUsed': 'not used yet',
  'owner.sessions.opened': 'opened {when}',
  'owner.sessions.expires': 'Expires {when}',
  'owner.sessions.empty.title': 'Nobody is signed in',
  'owner.sessions.empty.description': 'This company has no open sessions right now.',
  'owner.sessions.endOne.title': 'End this session?',
  'owner.sessions.endOne.body': '{name} is signed out on “{device}”. Other devices stay signed in.',
  'owner.sessions.endOne.confirm': 'End session',
  'owner.sessions.endOne.aria': 'End {name}’s session ({device})',
  'owner.sessions.endUser.body.one':
    '{count} session of {name} ends. The account stays active and can sign in again.',
  'owner.sessions.endUser.body.few':
    '{count} sessions of {name} end. The account stays active and can sign in again.',
  'owner.sessions.endUser.body.many':
    '{count} sessions of {name} end. The account stays active and can sign in again.',
  'owner.sessions.endAll.action': 'Sign everyone out',
  'owner.sessions.endAll.title': 'Sign everyone at {name} out?',
  'owner.sessions.endAll.body.one':
    '{count} person is signed out on every device ({sessions} open sessions). They will have to sign in again; no data is lost.',
  'owner.sessions.endAll.body.few':
    '{count} people are signed out on every device ({sessions} open sessions). They will have to sign in again; no data is lost.',
  'owner.sessions.endAll.body.many':
    '{count} people are signed out on every device ({sessions} open sessions). They will have to sign in again; no data is lost.',
  'owner.sessions.endAll.confirm': 'Sign everyone out',

  // ---- Workspace: activity log ------------------------------------------
  'owner.activity.loading': 'Loading the log…',
  'owner.activity.title': 'Company log',
  'owner.activity.description': 'Changes made by their people, and everything done about them from here.',
  'owner.activity.search.placeholder': 'Search descriptions',
  'owner.activity.search.aria': 'Search the log',
  'owner.activity.from': 'From',
  'owner.activity.to': 'To',
  'owner.activity.ownerOnly': 'Owner actions only',
  'owner.activity.ownerOnly.explain':
    'Suspensions, settings changes, sign-ins as a user with their reason, ended sessions and MES tests — everything done from this console.',
  'owner.activity.clear': 'Clear filters',
  'owner.activity.empty.title': 'No entries',
  'owner.activity.empty.description': 'Nothing has been logged for this company yet.',
  'owner.activity.empty.filtered': 'No entry matches the filters.',
  'owner.activity.ownerBadge': 'Owner',
  'owner.activity.prop.reason': 'Reason',
  'owner.activity.prop.settingsChanged': 'Settings changed',
  'owner.activity.prop.userName': 'User',
  'owner.activity.prop.device': 'Device',
  'owner.activity.prop.ended': 'Sessions ended',
  'owner.activity.causer': '{name} ({code})',
  'owner.activity.system': 'System',
  'owner.activity.impersonatedBy': 'as support: {name}',

  // ---- Workspace: access ------------------------------------------------
  'owner.access.modules.title': 'Modules',
  'owner.access.modules.description': 'Which parts of the app this company has. Switching one off deletes nothing.',
  'owner.access.module.submissions.off':
    'When off: workers cannot report a fault or an idea, and managers lose the report queue and tasks.',
  'owner.access.module.submissions.hint':
    'They disappear from the menu, and so does the new-report button. Nothing is deleted — switch it back on and everything is where it was.',
  'owner.access.module.fiveS.off': 'When off: no 5S walks, checklists or 5S analytics.',
  'owner.access.module.fiveS.hint':
    'Evaluators lose the new-walk button and the administrator loses the 5S checklists tab. Existing scores are kept.',
  'owner.access.module.analytics.off': 'When off: managers no longer see their company’s Analytics screen.',
  'owner.access.module.analytics.hint':
    'Reports and 5S are unaffected — only their overview and trends are hidden.',
  'owner.access.module.mes.off': 'When off: the nightly sync and the events sent to the MES stop.',
  'owner.access.module.mes.hint':
    'The credentials are kept, and the company’s administrator no longer sees the Integration tab.',

  'owner.access.security.title': 'Sign-in and security',
  'owner.access.security.description': 'How this company’s people sign in, and how long they stay signed in.',
  'owner.access.security.twoFactor': 'Two-factor protection for administrators',
  'owner.access.security.twoFactor.description':
    'An administrator without it can still sign in, but the admin section stays locked until they turn it on in their profile.',
  'owner.access.security.twoFactor.hint':
    'Applies to the administrator role only — workers and managers sign in as they always have.',
  'owner.access.security.password': 'Password sign-in',
  'owner.access.security.password.description':
    'When off, workers and managers sign in with their QR badge only. Administrators can always use a password.',
  'owner.access.security.password.hint':
    'The first sign-in with an activation code always works, whatever this says — that is how an account starts.',
  'owner.access.security.badge': 'QR badge sign-in',
  'owner.access.security.badge.description':
    'When off, printed badges stop working and everyone signs in with their code and password.',
  'owner.access.security.badge.hint':
    'Badges stay issued and work again as soon as this is back on — nothing has to be reprinted.',
  'owner.access.security.lastMethod': 'At least one way of signing in has to stay on.',
  'owner.access.security.sessionDays': 'Longest session (days)',
  'owner.access.security.sessionDays.hint':
    'How long “Remember me” keeps someone signed in, from 1 to 90. Without it a session lasts 12 hours. Applies to new sign-ins.',

  'owner.access.limits.title': 'Limits',
  'owner.access.limits.description': 'How many people and how much storage this company may use.',
  'owner.access.limits.seats': 'Seats',
  'owner.access.limits.seats.hint': 'Active users who are not owners. Leave empty for no limit.',
  'owner.access.limits.seats.explain':
    'Once the seats are full, the company’s administrator cannot add or reactivate a user. The nightly MES sync is never stopped — it only reports the overrun.',
  'owner.access.limits.storage': 'Attachment storage (MB)',
  'owner.access.limits.storage.hint': 'All of the company’s attachments together. Leave empty for no limit.',
  'owner.access.limits.storage.explain':
    'Once storage is full, new photos and videos are refused. Existing attachments stay.',
  'owner.access.limits.unlimited': 'No limit',
  'owner.access.limits.inUse': 'In use now',

  'owner.access.loss.module': 'The “{module}” module disappears from their app.',
  'owner.access.loss.password': 'Workers and managers can no longer sign in with a password — badge only.',
  'owner.access.loss.badge': 'Printed QR badges stop working.',
  'owner.access.loss.twoFactor':
    'Administrators without two-factor protection lose the admin section until they turn it on.',
  'owner.access.loss.seats':
    'The limit ({limit}) is below the number of active users ({used}) — nobody is deactivated, but no one new can be added.',
  'owner.access.confirm.title': 'Save the changes for {name}?',
  'owner.access.confirm.body': 'This reaches the company’s people straight away:',
  'owner.access.confirm.action': 'Save changes',
  'owner.access.dirty': 'You have unsaved changes.',
  'owner.access.clean': 'Everything is saved.',
  'owner.access.reset': 'Discard',
  'owner.access.saved': 'Access saved.',
  'owner.access.help':
    'Nothing here deletes anything: a module switched off or a limit only closes a door, and the data stays and comes back as soon as the change is undone. Every change is recorded in the company’s log.',

  // ---- Workspace: integration and settings ------------------------------
  'owner.mes.panel.title': 'KaproMES integration',
  'owner.mes.panel.description': 'The address, credentials and events we send to their MES.',
  'owner.mes.moduleOff.title': 'The module is off',
  'owner.mes.moduleOff.body':
    'The settings are kept, but the nightly sync and outgoing events skip this company until the module is switched on in the Access tab.',
  'owner.mes.locked.edit':
    'Your role ({role}) can test the connection and run the sync, but not change the settings. Allowed to change them: {editors}.',
  'owner.mes.locked.all':
    'Your role ({role}) can only look here. Allowed to test and sync: {runners}; to change: {editors}.',
  'owner.settings.title': 'Company details',
  'owner.settings.description': 'Name, code, contacts, logo and currency.',

  // ---- Team -------------------------------------------------------------
  'owner.team.title': 'Team',
  'owner.team.subtitle': 'The people who run the platform, and what each may do.',
  'owner.team.invite': 'Invite a member',
  'owner.team.invite.description': 'An account in the owner console, with the role you choose.',
  'owner.team.invite.warning':
    'Every team member sees every company’s data. What they may change depends on the role — give the smallest one that will do. We send an invitation by e-mail; they set the password themselves and nobody sees it.',
  'owner.team.invite.employeeCode.hint': 'This is what they sign in with.',
  'owner.team.invite.submit': 'Send the invitation',
  'owner.team.role': 'Role',
  'owner.team.role.aria': 'Role — {name}',
  'owner.team.panel.description': 'A role change applies at once, including in sessions already open.',
  'owner.team.you': 'you',
  'owner.team.status.active': 'active',
  'owner.team.status.inactive': 'deactivated',
  'owner.team.status.invited': 'invitation pending',
  'owner.team.lock.self':
    'You cannot change your own role or deactivate yourself — another member with full access does that.',
  'owner.team.lock.lastFull':
    'This is the only active member with full access. Give someone else full access first, or nobody could undo the change.',
  'owner.team.deactivate.action': 'Deactivate',
  'owner.team.deactivate.title': 'Deactivate {name}?',
  'owner.team.deactivate.body':
    'They lose access to the owner console at once. The account stays, and you can reactivate it.',
  'owner.team.deactivate.confirm': 'Deactivate',
  'owner.team.activate.action': 'Activate',

  // ---- Customer health ----------------------------------------------
  'owner.score.title': 'Customer health',
  'owner.score.description': 'Are they using what they bought — summed up in one score from 0 to 100.',
  'owner.score.explain':
    'A weighted mean of the measures below. A measure that cannot be worked out is left out, and its weight is shared among the rest. Worked out at most once every ten minutes.',
  'owner.score.loading': 'Working out the score…',
  'owner.score.computedAt': 'worked out {when}',
  'owner.score.lastActivity': 'Last activity {when}',
  'owner.score.noActivity': 'No activity yet',
  'owner.score.ring.aria': 'Health score {score} of 100 — {level}',
  'owner.score.components': 'What the score is made of',
  'owner.score.weight': 'weight {weight}',
  'owner.score.dropped.moduleOff': 'module off',
  'owner.score.dropped.noData': 'nothing to measure',

  'owner.score.level.healthy': 'Healthy',
  'owner.score.level.watch': 'Watch',
  'owner.score.level.atRisk': 'At risk',
  'owner.score.level.dormant': 'Dormant',
  'owner.score.level.new': 'New',
  'owner.score.level.suspended': 'Suspended',
  'owner.score.level.healthy.description':
    'Score 70 or more: they use it, spread it and answer their reports.',
  'owner.score.level.watch.description': 'Score 45 to 69: they use it, but something is slipping.',
  'owner.score.level.atRisk.description': 'Score under 45: use is weak or falling — worth a call.',
  'owner.score.level.dormant.description': 'Nobody has done anything in the last 30 days.',
  'owner.score.level.new.description': 'A customer for under 21 days — onboarding, no alarm.',
  'owner.score.level.suspended.description':
    'Access is suspended — the silence is our doing, not theirs.',

  'owner.score.component.recency': 'Last activity',
  'owner.score.component.reach': 'Reach',
  'owner.score.component.momentum': 'Momentum',
  'owner.score.component.responsiveness': 'Responsiveness',
  'owner.score.component.fiveS': '5S coverage',
  'owner.score.component.recency.hint':
    'Days since the latest activity of any kind — a report, a 5S walk, a review, a completed task or a sign-in. Full marks up to 3 days, zero from 30.',
  'owner.score.component.reach.hint':
    'The share of active users who did anything in the last 30 days. Full marks from 60%.',
  'owner.score.component.momentum.hint':
    'Activity in the last 30 days divided by activity in the 30 before. Full marks when it has not fallen, zero when it has stopped.',
  'owner.score.component.responsiveness.hint':
    'The share of reports from the last 90 days whose first response came within 72 hours — the same measure as in their own Analytics. Full marks from 90%.',
  'owner.score.component.fiveS.hint':
    'The share of active work centres with a 5S walk in the last 30 days. Full marks from 80%.',
  'owner.score.component.dropped':
    'This measure cannot be worked out for this customer, so it is left out of the score.',
  'owner.score.component.responsiveness.dropped.moduleOff':
    'The Reports module is off for this customer, so responsiveness is left out of the score.',
  'owner.score.component.responsiveness.dropped.noData':
    'No report from the last 90 days is older than 72 hours, so there is nothing to judge responsiveness by and it is left out of the score.',
  'owner.score.component.fiveS.dropped.moduleOff':
    'The 5S module is off for this customer, so 5S coverage is left out of the score.',
  'owner.score.component.fiveS.dropped.noData':
    'The company has no active work centre, so 5S coverage is left out of the score.',
  'owner.score.value.today': 'today',
  'owner.score.value.days.one': '{count} day ago',
  'owner.score.value.days.few': '{count} days ago',
  'owner.score.value.days.many': '{count} days ago',
  'owner.score.value.ratio': '{ratio}× the previous 30 days',
  'owner.score.value.reach': '{percent} of users',
  'owner.score.value.responsiveness': '{percent} of reports',
  'owner.score.value.fiveS': '{percent} of centres',

  'owner.score.warnings.title': 'Warnings',
  'owner.score.warnings.none': 'No warnings.',
  'owner.score.warnings.open': 'Open the {tab} tab',
  'owner.score.severity.critical': 'Critical',
  'owner.score.severity.warn': 'Warning',
  'owner.score.severity.info': 'Note',

  'owner.score.warning.dormant.one': 'No activity at all for {count} day.',
  'owner.score.warning.dormant.few': 'No activity at all for {count} days.',
  'owner.score.warning.dormant.many': 'No activity at all for {count} days.',
  'owner.score.warning.dormant.never': 'Nobody has ever done anything yet.',
  'owner.score.warning.activityDrop':
    'Activity fell from {previous} to {current} — the last 30 days against the 30 before.',
  'owner.score.warning.lowReach': 'Only {percent} of active users did anything in the last 30 days.',
  'owner.score.warning.neverActivated.one': '{count} user ({percent}) has never signed in.',
  'owner.score.warning.neverActivated.few': '{count} users ({percent}) have never signed in.',
  'owner.score.warning.neverActivated.many': '{count} users ({percent}) have never signed in.',
  'owner.score.warning.backlogOverdue.one': '{count} open report is past its due date.',
  'owner.score.warning.backlogOverdue.few': '{count} open reports are past their due date.',
  'owner.score.warning.backlogOverdue.many': '{count} open reports are past their due date.',
  'owner.score.warning.fiveSCoverageLow':
    'Only {percent} of active work centres had a 5S walk in the last 30 days.',
  'owner.score.warning.seatsNearlyFull': '{used} of {limit} seats are taken.',
  'owner.score.warning.storageNearlyFull': 'Attachment storage: {used} of {quota}.',
  'owner.score.warning.mesSyncStale': 'The last MES sync was {duration} ago.',
  'owner.score.warning.adminWithoutTwoFactor.one':
    '{count} administrator has no two-factor protection, and it is required.',
  'owner.score.warning.adminWithoutTwoFactor.few':
    '{count} administrators have no two-factor protection, and it is required.',
  'owner.score.warning.adminWithoutTwoFactor.many':
    '{count} administrators have no two-factor protection, and it is required.',
  'owner.score.warning.unknown': 'Warning from the server: {key}',

  // ---- Company list: health and activity ----------------------------
  'owner.companies.col.health': 'Health',
  'owner.companies.sort.aria': 'Order',
  'owner.companies.sort.name': 'By name',
  'owner.companies.sort.health': 'By health',
  'owner.companies.lastActivity': 'active {when}',
  'owner.companies.noActivity': 'no activity yet',
  'owner.companies.online': '{count} now',
  'owner.companies.warnings.one': '{count} warning',
  'owner.companies.warnings.few': '{count} warnings',
  'owner.companies.warnings.many': '{count} warnings',

  // ---- Workspace: analytics -----------------------------------------
  'owner.insights.window': "Whole months in the factory's local time, since {since}.",
  'owner.insights.moduleOff.title': 'Module switched off',
  'owner.insights.moduleOff.body':
    'The “{module}” module is switched off on the Access tab, so there is nothing to draw here. Data from while it was on has not been deleted.',

  'owner.insights.adoption.title': 'Adoption',
  'owner.insights.adoption.question':
    'Do people use the accounts they were given — and how many of them each month?',
  'owner.insights.funnel.title': 'From accounts to use',
  'owner.insights.funnel.question': 'How many of the accounts handed out are actually used?',
  'owner.insights.funnel.hint':
    'Active accounts that are not owners. Active means the person did something — a report, a 5S walk, a review, a completed task or a sign-in.',
  'owner.insights.funnel.accounts': 'Active accounts',
  'owner.insights.funnel.activated': 'Ever signed in',
  'owner.insights.funnel.active30': 'Active in 30 days',
  'owner.insights.funnel.active7': 'Active in 7 days',
  'owner.insights.funnel.empty': 'The company has no active accounts.',
  'owner.insights.contributors.title': 'Active people per month',
  'owner.insights.contributors.question': 'Is use spreading, or carried by a few people?',
  'owner.insights.contributors.hint': 'Distinct people with any activity that month.',
  'owner.insights.contributors.empty': 'Nobody did anything in this period.',
  'owner.insights.series.contributors': 'Active people',

  'owner.insights.process.title': 'Process',
  'owner.insights.process.question': 'Are reports handled in time, and do they become savings?',
  'owner.insights.stat.open': 'Open reports',
  'owner.insights.stat.overdue': 'Past due',
  'owner.insights.stat.implemented': 'Improvements carried out',
  'owner.insights.stat.implemented.detail': 'of {proposed} proposed',
  'owner.insights.stat.savings': 'Savings achieved',
  'owner.insights.stat.onTime': 'Tasks on time',
  'owner.insights.submissions.title': 'Reports per month',
  'owner.insights.submissions.question': 'How much is reported, and what?',
  'owner.insights.submissions.hint': 'Reports by the month they were filed, by type.',
  'owner.insights.submissions.empty': 'No reports in this period.',
  'owner.insights.response.title': 'Response and resolution',
  'owner.insights.response.question':
    'How fast does the first response come, and how long does resolving take?',
  'owner.insights.response.hint':
    'Median per month filed: from filing to the first response, and to resolution — the same measure as in their own Analytics.',
  'owner.insights.response.empty': 'No report from this period has had a response.',
  'owner.insights.series.firstResponse': 'First response',
  'owner.insights.series.resolution': 'Resolution',
  'owner.insights.unit.hours': '{count} h',
  'owner.insights.unit.days': '{count} d',
  'owner.insights.backlog.title': 'Open reports by age',
  'owner.insights.backlog.question': 'Is a backlog building up?',
  'owner.insights.backlog.hint': 'As of today, whatever period is chosen.',
  'owner.insights.backlog.summary': 'open {open} · past due {overdue}',
  'owner.insights.backlog.empty': 'No open reports.',
  'owner.insights.backlog.lt7': 'under 7 days',
  'owner.insights.backlog.lt30': '7–30 days',
  'owner.insights.backlog.lt90': '30–90 days',
  'owner.insights.backlog.gte90': '90+ days',
  'owner.insights.series.reports': 'Reports',
  'owner.insights.improvements.title': 'Improvements and savings',
  'owner.insights.improvements.question': 'Do proposals become real savings?',
  'owner.insights.improvements.hint':
    "Proposals filed in the chosen period, and savings by the date carried out, in the customer's currency.",
  'owner.insights.improvements.pipeline': 'From proposal to carried out',
  'owner.insights.improvements.proposed': 'proposed',
  'owner.insights.improvements.approved': 'approved',
  'owner.insights.improvements.implemented': 'carried out',
  'owner.insights.improvements.estimated': 'Estimated',
  'owner.insights.improvements.actual': 'Actual',
  'owner.insights.improvements.savings': 'Savings',
  'owner.insights.improvements.cost': 'Costs',
  'owner.insights.savings.empty': 'No improvement was carried out in this period.',
  'owner.insights.series.savings': 'Savings that month',
  'owner.insights.series.cumulative': 'Running total',
  'owner.insights.tasks.title': 'Tasks',
  'owner.insights.tasks.question': 'Are tasks finished on time?',
  'owner.insights.tasks.hint':
    'Tasks completed in the chosen period, and how many by their due date. Overdue open tasks are as of today.',
  'owner.insights.tasks.empty': 'No tasks in this period.',
  'owner.insights.tasks.completed': 'Completed',
  'owner.insights.tasks.onTime': 'Of those on time',
  'owner.insights.tasks.overdueOpen': 'Open, past due',

  'owner.insights.fiveS.title': '5S',
  'owner.insights.fiveS.question': 'Is workplace order improving, and is every centre being walked?',
  'owner.insights.fiveS.centres': 'active work centres: {count}',
  'owner.insights.fiveS.empty': 'No 5S walks in this period.',
  'owner.insights.fiveSTrend.title': 'Average and coverage',
  'owner.insights.fiveSTrend.question': 'Is the score rising, and what share of centres is walked?',
  'owner.insights.fiveSTrend.hint':
    'The average score of the month’s 5S walks, and the share of active work centres walked that month.',
  'owner.insights.walks.title': 'Walks per month',
  'owner.insights.walks.question': 'How many 5S walks actually happen?',
  'owner.insights.walks.hint': 'Submitted 5S walks by the month of the walk.',
  'owner.insights.series.average': 'Average score',
  'owner.insights.series.coverage': 'Coverage',
  'owner.insights.series.walks': 'Walks',

  'owner.insights.usage.title': 'Usage',
  'owner.insights.usage.question': 'When, where and on what do they work in the app?',
  'owner.insights.heatmap.title': 'When they work',
  'owner.insights.heatmap.question': 'On which days and at which hours do people use the app?',
  'owner.insights.heatmap.hint':
    "Reports, 5S walks, reviews and completed tasks in the last 90 days, in the factory's local time. Sign-ins are not included.",
  'owner.insights.heatmap.empty': 'No activity in the last 90 days.',
  'owner.insights.heatmap.peak': 'Busiest: {day}, {from}:00–{to}:00 ({count})',
  'owner.insights.heatmap.cell': '{day}, {from}:00–{to}:00: {count}',
  'owner.insights.heatmap.hours': "Hour of the day, the factory's local time",
  'owner.insights.heatmap.less': 'less',
  'owner.insights.heatmap.more': 'more',
  'owner.insights.plants.title': 'By plant',
  'owner.insights.plants.question': 'Which plant carries the use, and which lags behind?',
  'owner.insights.plants.hint':
    'The last 90 days. Active people are those who did something in that plant.',
  'owner.insights.plants.empty': 'No activity by plant in the last 90 days.',
  'owner.insights.plants.plant': 'Plant',
  'owner.insights.plants.submissions': 'Reports',
  'owner.insights.plants.evaluations': '5S',
  'owner.insights.plants.activeUsers': 'People',
  'owner.insights.plants.none': 'No plant',
  'owner.insights.modules.title': 'Modules',
  'owner.insights.modules.question': 'Do they use what is switched on?',
  'owner.insights.modules.hint':
    'Actions in the last 30 days. Reports: new reports and status changes. 5S: walks. KaproMES: successful syncs and deliveries, where recorded. Analytics is not measured — opening a screen is not recorded anywhere.',
  'owner.insights.modules.on': 'on',
  'owner.insights.modules.off': 'off',
  'owner.insights.modules.notTracked': 'not measured',
  'owner.insights.modules.actions.one': '{count} action',
  'owner.insights.modules.actions.few': '{count} actions',
  'owner.insights.modules.actions.many': '{count} actions',
  'owner.insights.devices.title': 'Devices',
  'owner.insights.devices.question': 'Do they work from a phone at the machine, or from an office?',
  'owner.insights.devices.hint':
    'Sessions opened in the chosen period, by browser data. Devices have been recorded since {since}.',
  'owner.insights.devices.hint.never': 'Sessions opened in the chosen period, by browser data.',
  'owner.insights.devices.empty': 'No session with a recorded device yet.',
  'owner.insights.devices.unknown': 'unknown',
  'owner.insights.devices.total': 'sessions in total: {count}',
  'owner.insights.devices.sessions.one': '{count} session',
  'owner.insights.devices.sessions.few': '{count} sessions',
  'owner.insights.devices.sessions.many': '{count} sessions',

  'owner.weekday.short.1': 'Mon',
  'owner.weekday.short.2': 'Tue',
  'owner.weekday.short.3': 'Wed',
  'owner.weekday.short.4': 'Thu',
  'owner.weekday.short.5': 'Fri',
  'owner.weekday.short.6': 'Sat',
  'owner.weekday.short.7': 'Sun',
  'owner.weekday.1': 'Monday',
  'owner.weekday.2': 'Tuesday',
  'owner.weekday.3': 'Wednesday',
  'owner.weekday.4': 'Thursday',
  'owner.weekday.5': 'Friday',
  'owner.weekday.6': 'Saturday',
  'owner.weekday.7': 'Sunday',

  // ---- Portfolio ----------------------------------------------------
  'owner.portfolio.benchmarks.title': 'Medians',
  'owner.portfolio.benchmarks.description':
    'The middle value among active customers past onboarding — half are above it, half below.',
  'owner.portfolio.title': 'Customers side by side',
  'owner.portfolio.description':
    'Click a row to open the customer. Click a column heading to sort by it — those needing attention first.',
  'owner.portfolio.empty': 'No customers yet.',
  'owner.portfolio.sort.aria': 'Sort by {column}',
  'owner.portfolio.worse': 'worse than the median ({median})',
  'owner.portfolio.legend.worse': 'worse than the median of all customers',
  'owner.portfolio.legend.moduleOff': 'module off — an earlier figure, not compared',
  'owner.portfolio.moduleOff':
    'This module is off for this customer. The figure is from while it was on and is not compared with the median.',
  'owner.portfolio.benchmarks.customers.one': 'across {count} customer',
  'owner.portfolio.benchmarks.customers.few': 'across {count} customers',
  'owner.portfolio.benchmarks.customers.many': 'across {count} customers',
  'owner.portfolio.trend.up': 'more than the previous 30 days',
  'owner.portfolio.trend.down': 'fewer than the previous 30 days',
  'owner.portfolio.trend.flat': 'the same as the previous 30 days',
  'owner.portfolio.trend.none': 'no previous 30 days to compare with',
  'owner.portfolio.metric.company': 'Company',
  'owner.portfolio.metric.health': 'Health',
  'owner.portfolio.metric.health.hint':
    "The health score from 0 to 100 — what it is made of is on each customer's Overview tab.",
  'owner.portfolio.metric.reach': 'Reach',
  'owner.portfolio.metric.reach.hint': 'The share of active users with any activity in the last 30 days.',
  'owner.portfolio.metric.submissions': 'Reports, 30 days',
  'owner.portfolio.metric.submissions.hint':
    'Reports filed in the last 30 days; the arrow is the change against the 30 before.',
  'owner.portfolio.metric.response': 'Median response',
  'owner.portfolio.metric.response.hint':
    'The median time from filing to the first response, for reports from the last 90 days.',
  'owner.portfolio.metric.overdue': 'Past due',
  'owner.portfolio.metric.overdue.hint': 'Open reports past their due date, as of today.',
  'owner.portfolio.metric.fiveSAverage': '5S average',
  'owner.portfolio.metric.fiveSAverage.hint': 'The average score of 5S walks in the last 30 days.',
  'owner.portfolio.metric.fiveSCoverage': '5S coverage',
  'owner.portfolio.metric.fiveSCoverage.hint':
    'The share of active work centres walked in the last 30 days.',
  'owner.portfolio.metric.savings': 'Savings this year',
  'owner.portfolio.metric.savings.hint':
    "Actual savings of improvements carried out this calendar year, in the customer's currency.",
  'owner.portfolio.metric.lastActivity': 'Last activity',
  'owner.portfolio.metric.lastActivity.hint':
    'Anything: a report, a 5S walk, a review, a completed task or a sign-in. A green dot means somebody is in the app right now.',
  'owner.portfolio.platform.title': 'The whole platform',
  'owner.portfolio.platform.description': 'All customers together, over the chosen period.',

  // ---- Phase 3: what is recorded, and since when ---------------------
  'owner.status.unknown': 'Unknown ({value})',
  'owner.status.unknown.hint': 'This value is newer than this version of the app.',
  'owner.duration.ms': '{count} ms',
  'owner.duration.seconds': '{count} s',
  'owner.duration.minutes': '{minutes} min {seconds} s',
  'owner.duration.hours': '{hours} h {minutes} min',
  'owner.due.now': 'now',
  'owner.due.minutes': 'in {count} min',
  'owner.due.hours': 'in {count} h',
  'owner.due.days.one': 'in {count} day',
  'owner.due.days.few': 'in {count} days',
  'owner.due.days.many': 'in {count} days',

  // ---- Workspace: security -----------------------------------------
  'owner.workspace.tab.security': 'Security',
  'owner.security.loading': 'Loading sign-ins…',
  'owner.security.window': 'Days in the factory’s local time, from {since}',
  'owner.security.recordingSince': 'Sign-in attempts are recorded from {date}',
  'owner.security.recordingSince.partial':
    'Sign-in attempts are only recorded from {date} — the days before that are not empty, they were not recorded.',
  'owner.security.period.aria': 'Period',
  'owner.security.period.one': 'Last {count} day',
  'owner.security.period.few': 'Last {count} days',
  'owner.security.period.many': 'Last {count} days',
  'owner.security.notRecorded.title': 'Sign-ins are not recorded yet',
  'owner.security.notRecorded.description':
    'Recording sign-in attempts starts with this version. The first attempt after that — successful or not — will appear here. Until then there is nothing to show: a zero would mean “nobody got it wrong” when the truth is “nobody was counting”.',

  'owner.security.stat.succeeded': 'Successful sign-ins',
  'owner.security.stat.succeeded.hint':
    'Sign-ins that ended inside the app in the chosen period, by password or by badge. Below: how many different people got in, out of all active accounts.',
  'owner.security.stat.succeeded.detail': '{signedIn} of {accounts} people',
  'owner.security.stat.failed': 'Failed',
  'owner.security.stat.failed.hint':
    'Attempts with something wrong: an unknown code or badge, a wrong password or code, an inactive account. Below: refused (right details, closed door) and throttled (too many attempts).',
  'owner.security.stat.failed.detail': 'refused {refused} · throttled {throttled}',
  'owner.security.stat.rate': 'Failure rate',
  'owner.security.stat.rate.hint':
    'Failed attempts out of failed and successful together. Up to 15% is normal, because people mistype; from 30% it is either a wrongly shared password or somebody who is not one of theirs.',
  'owner.security.rate.normal': 'normal',
  'owner.security.rate.elevated': 'elevated',
  'owner.security.rate.high': 'high',
  'owner.security.rate.none': 'no attempts',
  'owner.security.stat.twoFactor': 'Admin 2FA',
  'owner.security.stat.twoFactor.value': '{covered} / {admins}',
  'owner.security.stat.twoFactor.hint':
    'Active administrators with two-factor protection switched on. When it is required, an administrator without it cannot open the admin section. All accounts: {covered} of {accounts}.',
  'owner.security.twoFactor.required': 'required',
  'owner.security.twoFactor.optional': 'not required',
  'owner.security.stat.badges': 'QR badges',
  'owner.security.stat.badges.hint':
    'Active accounts holding a QR sign-in badge. Below: successful sign-ins by password and by badge in the chosen period.',
  'owner.security.stat.badges.detail': 'password {password} · badge {badge}',
  'owner.security.stat.lastSuccess': 'Last successful',
  'owner.security.stat.lastSuccess.hint':
    'The last successful sign-in by anybody at this company, whatever the chosen period.',

  'owner.security.chart.title': 'Sign-ins by day',
  'owner.security.chart.question': 'Do people get in without trouble, and when were there failures?',
  'owner.security.chart.hint':
    'Every sign-in attempt by local day and outcome. A request for the second step is not drawn: its outcome is the next entry. Days before recording began are not drawn.',
  'owner.security.chart.empty': 'No sign-in attempts in this period.',
  'owner.security.chart.aria':
    'Sign-in attempts by day, {days} days: {succeeded} successful, {failed} failed, {refused} refused, {throttled} throttled.',
  'owner.security.series.succeeded': 'Successful',
  'owner.security.series.failed': 'Failed',
  'owner.security.series.refused': 'Refused',
  'owner.security.series.throttled': 'Throttled',

  'owner.security.failures.one': '{count} failed attempt',
  'owner.security.failures.few': '{count} failed attempts',
  'owner.security.failures.many': '{count} failed attempts',
  'owner.security.showAttempts': 'Attempts',
  'owner.security.showAttempts.person': 'Show the sign-in attempts of {name}',
  'owner.security.showAttempts.ip': 'Show the sign-in attempts from {ip}',

  'owner.security.repeated.title': 'Repeated failures',
  'owner.security.repeated.question': 'Who cannot get in — or who is trying somebody else’s account?',
  'owner.security.repeated.hint':
    'People with three or more failed attempts in the chosen period, at most ten, most failures first. The name opens the person on the People tab.',
  'owner.security.repeated.none': 'Nobody had three or more failed attempts.',
  'owner.security.repeated.throttled': 'throttled: {count}',
  'owner.security.repeated.lastFailure': 'last failure {when}',
  'owner.security.repeated.lastSuccess': 'last successful sign-in {when}',
  'owner.security.repeated.noSuccess': 'no successful sign-in at all',

  'owner.security.afterFailures.title': 'Success after a run of failures',
  'owner.security.afterFailures.question': 'Did somebody guess a password?',
  'owner.security.afterFailures.hint':
    'A successful sign-in preceded by five or more failed attempts on the same account within the hour before it. It may be the person remembering on the sixth try — which is why they should be asked.',
  'owner.security.afterFailures.alert':
    'This is what a guessed password looks like. Check with the person whether it was them; if not, end their sessions and have them change the password.',
  'owner.security.afterFailures.none': 'No successful sign-in came after a run of failed ones.',
  'owner.security.afterFailures.count': 'Failed attempts in the hour before: {count}',
  'owner.security.afterFailures.at': 'signed in {when}',

  'owner.security.ips.title': 'Addresses with the most failures',
  'owner.security.ips.question': 'Where do the wrong attempts come from?',
  'owner.security.ips.hint':
    'IP addresses with three or more failed attempts on this company’s accounts in the chosen period, at most ten. Many accounts from one address means somebody is working down a list.',
  'owner.security.ips.none': 'No address had three or more failed attempts.',
  'owner.security.ips.accounts': 'accounts: {count}',
  'owner.security.ips.lastAt': 'last {when}',

  'owner.security.log.title': 'Sign-in log',
  'owner.security.log.hint':
    'Every sign-in attempt, newest first. What was typed — password, code, badge — is never stored, so it is not here either.',
  'owner.security.log.description':
    'Narrow by outcome, person or address. Attempts on an unknown code or badge have no person.',
  'owner.security.log.panel': 'Sign-in attempts',
  'owner.security.log.loading': 'Loading the sign-in log…',
  'owner.security.log.empty.title': 'No sign-in attempts',
  'owner.security.log.empty.description': 'No sign-in attempt has been recorded for this company yet.',
  'owner.security.log.empty.filtered': 'No attempt matches the filters.',
  'owner.security.log.unknownAccount': 'no account found',
  'owner.security.log.unknownBadge': 'badge not recognised',
  'owner.security.col.when': 'Time',
  'owner.security.col.person': 'User',
  'owner.security.col.method': 'Method',
  'owner.security.col.outcome': 'Outcome',
  'owner.security.filter.outcome': 'Outcome',
  'owner.security.filter.allOutcomes': 'All outcomes',
  'owner.security.filter.reason': 'Reason',
  'owner.security.filter.allReasons': 'All reasons',
  'owner.security.filter.person': 'User',
  'owner.security.filter.everybody': 'All users',
  'owner.security.filter.ip': 'IP address',
  'owner.security.filter.ip.placeholder': 'IP address',
  'owner.security.legend.title': 'What the outcomes and reasons mean',

  'owner.security.outcome.succeeded': 'Successful',
  'owner.security.outcome.succeeded.hint':
    'The person got in: the details were right, and where a second step is required, they passed it too.',
  'owner.security.outcome.challenged': 'Second step asked',
  'owner.security.outcome.challenged.hint':
    'The password or badge was right and the two-factor code was asked for. The second step’s outcome is recorded as an entry of its own.',
  'owner.security.outcome.failed': 'Failed',
  'owner.security.outcome.failed.hint':
    'Something was wrong — an unknown code or badge, a wrong password or code, an inactive account. The reason is beside the entry.',
  'owner.security.outcome.refused': 'Refused',
  'owner.security.outcome.refused.hint':
    'The details were right but entry is not allowed: the company is suspended or that way of signing in is switched off.',
  'owner.security.outcome.throttled': 'Throttled',
  'owner.security.outcome.throttled.hint':
    'Too many attempts in a short time, so the server refuses without checking the details. A guard against guessing; it lifts by itself after a short while.',
  'owner.security.outcome.unknown': 'Unknown outcome ({value})',
  'owner.security.outcome.unknown.hint': 'This outcome is newer than this version of the app.',
  'owner.security.reason.unknownAccount': 'Unknown code',
  'owner.security.reason.unknownAccount.hint':
    'No account has that employee code. Repeated attempts at the same unknown code are grouped without keeping what was typed, because people sometimes type their password into the code box.',
  'owner.security.reason.wrongPassword': 'Wrong password',
  'owner.security.reason.wrongPassword.hint': 'The account exists; the password is not right.',
  'owner.security.reason.inactiveAccount': 'Inactive account',
  'owner.security.reason.inactiveAccount.hint':
    'The password is right but the account is deactivated. The company’s administrator can reactivate it.',
  'owner.security.reason.unknownBadge': 'Unknown badge',
  'owner.security.reason.unknownBadge.hint':
    'The scanned badge belongs to no account — it was revoked, replaced by a new one, or it is somebody else’s.',
  'owner.security.reason.wrongCode': 'Wrong code',
  'owner.security.reason.wrongCode.hint':
    'The six-digit code from the two-factor app is not right or has expired.',
  'owner.security.reason.wrongRecoveryCode': 'Wrong backup code',
  'owner.security.reason.wrongRecoveryCode.hint': 'The backup code is not right or has already been used.',
  'owner.security.reason.challengeExpired': 'Second step expired',
  'owner.security.reason.challengeExpired.hint':
    'The second step was not finished in time, or the account changed in between — it was deactivated or its two-factor protection was switched off.',
  'owner.security.reason.companySuspended': 'Company suspended',
  'owner.security.reason.companySuspended.hint':
    'The details are right but the company’s access is suspended. The user sees the reason on the sign-in screen.',
  'owner.security.reason.methodDisabled': 'Sign-in method off',
  'owner.security.reason.methodDisabled.hint':
    'The details are right but that way of signing in — password or badge — is switched off for this company on the Access tab.',
  'owner.security.reason.unknown': 'Reason: {value}',
  'owner.security.reason.unknown.hint': 'This reason is newer than this version of the app.',
  'owner.security.method.password': 'Password',
  'owner.security.method.badge': 'QR badge',
  'owner.security.method.unknown': 'Unknown ({value})',
  'owner.security.secondFactor.totp': 'app code',
  'owner.security.secondFactor.recoveryCode': 'backup code',

  // ---- MES sync history (owner and admin Integration) -------------------
  'owner.mes.runs.title': 'Syncs',
  'owner.mes.runs.description': 'Every sync run — nightly, from the owner console or by the company’s administrator.',
  'owner.mes.runs.hint':
    'Recorded from this version on. Activation codes a sync issued are only counted here — they are never stored.',
  'owner.mes.runs.none':
    'No sync recorded yet. Runs are recorded from this version on, so even an integration that has worked for months starts empty here; the first nightly or manual run will appear here.',
  'owner.mes.runs.lastSuccess': 'Last successful',
  'owner.mes.runs.lastFailure': 'Last failed',
  'owner.mes.runs.noFailure': 'none',
  'owner.mes.runs.consecutive': 'Failures in a row',
  'owner.mes.runs.consecutive.hint':
    'Failed or interrupted runs since the last successful one. One is worth a look; three mean people and machines have not been updated for days.',
  'owner.mes.runs.successRate': 'Success, 30 days',
  'owner.mes.runs.successRate.hint': 'The share of finished runs in the last 30 days that succeeded.',
  'owner.mes.runs.severity.ok': 'fine',
  'owner.mes.runs.severity.warn': 'attention',
  'owner.mes.runs.severity.critical': 'urgent',
  'owner.mes.runs.failingInARow.one': '{count} run in a row has failed.',
  'owner.mes.runs.failingInARow.few': '{count} runs in a row have failed.',
  'owner.mes.runs.failingInARow.many': '{count} runs in a row have failed.',
  'owner.mes.runs.recent': 'Last five',
  'owner.mes.runs.activations': 'activation codes issued: {count}',
  'owner.mes.runs.problems.one': '{count} problem',
  'owner.mes.runs.problems.few': '{count} problems',
  'owner.mes.runs.problems.many': '{count} problems',
  'owner.mes.runs.problemsMore': 'And {count} more — the first 50 are kept.',
  'owner.mes.runs.showAll': 'Show all',
  'owner.mes.runs.hideAll': 'Hide',
  'owner.mes.runs.status.running': 'Running',
  'owner.mes.runs.status.running.hint': 'The sync has started and is still going.',
  'owner.mes.runs.status.succeeded': 'Succeeded',
  'owner.mes.runs.status.succeeded.hint':
    'Finished without an error. Problems it found — machines without a 5S checklist, say — are listed below.',
  'owner.mes.runs.status.failed': 'Failed',
  'owner.mes.runs.status.failed.hint':
    'Stopped by an error, most often because the MES did not answer or refused the token. The error message is below.',
  'owner.mes.runs.status.interrupted': 'Interrupted',
  'owner.mes.runs.status.interrupted.hint':
    'Started over an hour ago and never finished — the process was killed half-way, by a server restart for instance.',
  'owner.mes.runs.trigger.command': 'Nightly',
  'owner.mes.runs.trigger.command.hint': 'The scheduled sync the server runs every night.',
  'owner.mes.runs.trigger.owner': 'Owner console',
  'owner.mes.runs.trigger.owner.hint': 'Started with “Sync now” in the owner console.',
  'owner.mes.runs.trigger.admin': 'Company administrator',
  'owner.mes.runs.trigger.admin.hint': 'Started with “Sync now” on the company’s Integration screen.',

  // ---- Workspace: storage and seats over time --------------------------
  'owner.usage.projection.storage.one': 'The quota will be full in ~{count} day.',
  'owner.usage.projection.storage.few': 'The quota will be full in ~{count} days.',
  'owner.usage.projection.storage.many': 'The quota will be full in ~{count} days.',
  'owner.usage.projection.storage.full': 'The quota is already full — new photos and videos are refused.',
  'owner.usage.projection.seats.one': 'The seats will be taken in ~{count} day.',
  'owner.usage.projection.seats.few': 'The seats will be taken in ~{count} days.',
  'owner.usage.projection.seats.many': 'The seats will be taken in ~{count} days.',
  'owner.usage.projection.seats.full': 'Every seat is taken — no new user can be added.',
  'owner.usage.projection.short.one': '~{count} day',
  'owner.usage.projection.short.few': '~{count} days',
  'owner.usage.projection.short.many': '~{count} days',
  'owner.usage.projection.short.full': 'already full',
  'owner.usage.projection.storage.distant': 'At this rate the quota will not be full for over ten years.',
  'owner.usage.projection.seats.distant': 'At this rate the seats will not be taken for over ten years.',
  'owner.usage.projection.short.distant': 'over 10 years',
  'owner.usage.growth.storage': '{amount} a day',
  'owner.usage.growth.seats': '{amount} in 30 days',
  'owner.usage.growth.flat': 'no growth',
  'owner.usage.history.notYet': 'The daily record starts tonight — the line appears from tomorrow.',
  'owner.usage.history.since': 'Recorded from {date} — the line needs at least two days.',
  'owner.usage.history.unavailable': 'The history is not available right now.',
  'owner.usage.spark.storage': 'Storage used over {days} days: from {from} to {to}.',
  'owner.usage.spark.seats': 'Seats taken over {days} days: from {from} to {to}.',

  // ---- System: recorded history --------------------------------------
  'owner.system.loading': 'Loading the history…',
  'owner.system.recordingSince': 'Recorded from {date}',

  'owner.system.tasks.title': 'Scheduled tasks',
  'owner.system.tasks.question': 'Is the scheduler running — and did every task run when it was due?',
  'owner.system.tasks.hint':
    'Every task in the app’s schedule, in schedule order: its last run, how long it took and when it is next due.',
  'owner.system.tasks.count.one': '{count} scheduled task',
  'owner.system.tasks.count.few': '{count} scheduled tasks',
  'owner.system.tasks.count.many': '{count} scheduled tasks',
  'owner.system.tasks.notRecorded':
    'Runs are not recorded yet — the first scheduler run after this version will appear here.',
  'owner.system.tasks.empty': 'The app’s schedule is empty.',
  'owner.system.tasks.summary.late': 'late: {count}',
  'owner.system.tasks.summary.failing': 'failing: {count}',
  'owner.system.tasks.summary.ok': 'all on time',
  'owner.system.tasks.col.task': 'Task',
  'owner.system.task.remindOverdue': 'Morning overdue reminder',
  'owner.system.task.remindEvaluations': 'Monthly 5S reminder',
  'owner.system.task.syncMes': 'KaproMES sync',
  'owner.system.task.sendMesReports': 'Ratings and 5S summary to KaproMES',
  'owner.system.task.snapshotUsage': 'Daily storage and seats record',
  'owner.system.task.evaluateAlerts': 'Alert check',
  'owner.system.task.alertDigest': 'Morning alert digest by e-mail',
  'owner.system.task.pruneSignIns': 'Clearing old sign-ins',
  'owner.system.task.pruneHistory': 'Clearing old history',
  'owner.system.tasks.col.status': 'State',
  'owner.system.tasks.col.status.hint':
    'Succeeded, Failed, Running, Skipped (the previous run is still going, or another server took it) or Interrupted (killed half-way). “Late” comes first: a task that stopped being run still shows the green of its last run.',
  'owner.system.tasks.col.lastRun': 'Last run',
  'owner.system.tasks.col.duration': 'Duration',
  'owner.system.tasks.col.next': 'Next',
  'owner.system.tasks.col.next.hint':
    'From the schedule’s expression, in its time zone. The exact time is under the pointer.',
  'owner.system.tasks.col.failures': 'Failures, 7 days',
  'owner.system.tasks.col.failures.hint': 'Failed runs in the last seven days, out of all runs.',
  'owner.system.tasks.late': 'Late',
  'owner.system.tasks.late.hint':
    'It was due more than 15 minutes ago and no run has been recorded. Most often this means schedule:work is not running.',
  'owner.system.tasks.neverRun': 'Not run yet',
  'owner.system.tasks.neverRun.hint': 'This task has not been due once since recording began.',
  'owner.system.tasks.exitCode': 'exit code {code}',
  'owner.system.tasks.status.running': 'Running',
  'owner.system.tasks.status.running.hint': 'Started and still going. A background task is closed when it finishes.',
  'owner.system.tasks.status.succeeded': 'Succeeded',
  'owner.system.tasks.status.succeeded.hint': 'Finished with exit code 0.',
  'owner.system.tasks.status.failed': 'Failed',
  'owner.system.tasks.status.failed.hint':
    'Finished with an error or a non-zero exit code. The message is below, if there is one.',
  'owner.system.tasks.status.skipped': 'Skipped',
  'owner.system.tasks.status.skipped.hint':
    'The scheduler skipped it because the previous run is still going or another server took it — as designed.',
  'owner.system.tasks.status.interrupted': 'Interrupted',
  'owner.system.tasks.status.interrupted.hint':
    'Started over an hour ago and never finished — the process was killed half-way.',

  'owner.system.mes.title': 'KaproMES syncs',
  'owner.system.mes.question': 'Does the nightly sync reach every customer who has it?',
  'owner.system.mes.panel': 'Customers with the MES configured',
  'owner.system.mes.hint':
    'Every customer with an MES address configured, by name. The name opens their Integration tab with the full history.',
  'owner.system.mes.empty': 'No customer has KaproMES configured.',
  'owner.system.mes.noRun': 'no run recorded yet',
  'owner.system.mes.col.lastRun': 'Last run',
  'owner.system.mes.col.deliveries': 'Deliveries',
  'owner.system.mes.col.deliveries.hint': 'Events sent to their MES: failed, and still waiting.',
  'owner.system.mes.failed': 'failed {count}',
  'owner.system.mes.pending': 'waiting {count}',

  'owner.system.storage.title': 'Storage growth',
  'owner.system.storage.question': 'How fast is the disk filling up, and who is filling it?',
  'owner.system.storage.total': 'All attachments',
  'owner.system.storage.total.hint': 'Every attachment of every customer, now.',
  'owner.system.storage.growth': 'Growth',
  'owner.system.storage.growth.hint':
    'The sum of every customer’s daily growth — a straight line through their last 30 days. It needs at least seven daily records.',
  'owner.system.storage.growth.none': 'too few daily records yet',
  'owner.system.storage.diskFull': 'Disk full in',
  'owner.system.storage.diskFull.hint':
    'Free space divided by the total daily growth. Approximate: a straight line does not know about the next customer.',
  'owner.system.storage.diskFull.none': 'no growth, or the disk cannot be measured',
  'owner.system.storage.byCompany': 'By customer',
  'owner.system.storage.byCompany.hint':
    'Every customer, largest first: used now, the quota, daily growth and when the quota will be full.',
  'owner.system.storage.col.used': 'Used',
  'owner.system.storage.col.quota': 'Quota',
  'owner.system.storage.col.full': 'Quota full in',

  'owner.system.signIns.title': 'Sign-ins to the platform',
  'owner.system.signIns.question': 'Who is trying to get in, to which company — and is anybody trying our team?',
  'owner.system.signIns.notRecorded':
    'Recording sign-in attempts starts with this version. The first attempt after that, by any customer or owner, will appear here.',
  'owner.system.signIns.challenged': 'second step asked: {count}',
  'owner.system.signIns.chart.hint':
    'Every attempt on the platform by local day: customers, our team, and attempts that belong to nobody.',
  'owner.system.signIns.owners.title': 'Owner sign-ins',
  'owner.system.signIns.owners.hint':
    'Our team’s accounts open every customer, so even one failure is worth a look, and five or a throttle is urgent.',
  'owner.system.signIns.owners.none': 'No failed attempt on an owner account in the chosen period.',
  'owner.system.signIns.owners.failed': 'Failed attempts on owner accounts: {failed} · throttled: {throttled}',
  'owner.system.signIns.owners.lastFailure': 'Last failure',
  'owner.system.signIns.owners.lastSuccess': 'Last successful',
  'owner.system.signIns.unknown.title': 'Unknown codes and badges',
  'owner.system.signIns.unknown.hint':
    'Attempts that matched no account, so they belong to no company. This is where somebody guessing shows up first.',
  'owner.system.signIns.unknown.ips.hint': 'Addresses with the most attempts at unknown codes and badges, at most ten.',
  'owner.system.signIns.codes.title': 'Repeated unknown codes',
  'owner.system.signIns.codes.hint':
    'The same unknown code typed three or more times. The key is the start of a keyed fingerprint of what was typed — enough to see that one code was tried forty times, and nothing that reveals which.',
  'owner.system.signIns.codes.key': 'Attempt key — does not reveal what was typed',
  'owner.system.signIns.codes.none': 'No unknown code was tried three times.',
  'owner.system.signIns.codes.attempts': 'attempts: {count}',
  'owner.system.signIns.byCompany': 'By customer',
  'owner.system.signIns.byCompany.hint':
    'Every customer by name, for the chosen period. The name opens their Security tab.',
  'owner.system.signIns.col.repeated': 'Repeated failures',
  'owner.system.signIns.repeated': 'people: {count}',

  // ---- Phase 4: the overview, rebuilt around alerts ------------------
  'owner.dashboard.system.aria': 'System status',
  'owner.dashboard.system.open': '{label}: {value}. Open the System page.',
  'owner.dashboard.system.unknown': 'nothing recorded yet',
  'owner.dashboard.system.queue.ok': 'running',
  'owner.dashboard.system.queue.backlog': 'a job has waited {minutes} min',
  'owner.dashboard.system.queue.failing': 'failed in 24 h: {count}',
  'owner.dashboard.system.mes.ok': 'working for all ({count})',
  'owner.dashboard.system.mes.failing': 'failing: {failing} of {configured}',
  'owner.dashboard.system.mes.none': 'no customer',
  'owner.dashboard.system.disk': 'Disk',
  'owner.dashboard.system.disk.free': '{free} free',
  'owner.dashboard.system.disk.full': 'full',
  'owner.dashboard.system.disk.days.one': 'full in ~{count} day',
  'owner.dashboard.system.disk.days.few': 'full in ~{count} days',
  'owner.dashboard.system.disk.days.many': 'full in ~{count} days',

  'owner.dashboard.customers.title': 'Customers',
  'owner.dashboard.customers.description': 'The ones who need attention come first. A tile opens the customer.',
  'owner.dashboard.customers.empty': 'No customers yet.',
  'owner.dashboard.customers.alerts': 'Open alerts — urgent: {critical}, warnings: {warn}, notes: {info}',
  'owner.dashboard.customers.noAlerts': 'no open alerts',

  'owner.dashboard.pulse.title': 'Platform pulse',
  'owner.dashboard.pulse.description': 'Today and this month, against the same days last month. Customers only.',
  'owner.dashboard.pulse.hint':
    '“The same days last month” means from the 1st to today’s day of the month, so half a month is never compared with a whole one. The line is the last 30 days, in local time.',
  'owner.dashboard.pulse.submissions': 'Reports',
  'owner.dashboard.pulse.submissions.hint': 'Reports filed by every customer.',
  'owner.dashboard.pulse.evaluations': '5S evaluations',
  'owner.dashboard.pulse.evaluations.hint': '5S walks handed in by every customer.',
  'owner.dashboard.pulse.signIns': 'Sign-ins',
  'owner.dashboard.pulse.signIns.hint': 'Successful sign-ins to the application, by password or badge.',
  'owner.dashboard.pulse.activePeople': 'Active people',
  'owner.dashboard.pulse.activePeople.hint':
    'Distinct people at customers who did anything or signed in successfully in the period.',
  'owner.dashboard.pulse.newUsers': 'New accounts',
  'owner.dashboard.pulse.newUsers.hint': 'Customer accounts opened in the period.',
  'owner.dashboard.pulse.today': 'today',
  'owner.dashboard.pulse.thisMonth': 'this month',
  'owner.dashboard.pulse.month': 'this month: {count}',
  'owner.dashboard.pulse.change.up': '▲ {percent} more than the same days last month ({previous})',
  'owner.dashboard.pulse.change.down': '▼ {percent} fewer than the same days last month ({previous})',
  'owner.dashboard.pulse.change.same': 'the same as the same days last month ({previous})',
  'owner.dashboard.pulse.change.fromZero': 'the same days last month: 0',
  'owner.dashboard.pulse.spark': '{label} per day, the last {days} days: {total} in all, at most {peak} in one day.',

  'owner.dashboard.activity.title': 'What the team did',
  'owner.dashboard.activity.description': 'The last 15 owner actions, across every customer.',
  'owner.dashboard.activity.empty': 'No owner action recorded yet.',
  'owner.dashboard.activity.impersonating': 'in a support session',
  'owner.dashboard.activity.impersonating.hint':
    'Done while the owner was signed in as one of the customer’s people.',
  'owner.dashboard.activity.open': 'Open the activity log of {name}',

  // ---- Alerts ---------------------------------------------------------
  // "Take it", "snooze", "mute": the three things an owner does to an alert.
  // Nothing is closed by hand — an alert resolves when its cause goes away.
  'owner.alerts.title': 'Needs attention',
  'owner.alerts.description': 'Everything the checks find, in one place — at customers and on the platform.',
  'owner.alerts.hint':
    'The checks run every ten minutes. An alert closes itself when its cause goes away; the Resolved tab keeps what was open and who dealt with it.',
  'owner.alerts.loading': 'Loading alerts…',
  'owner.alerts.tabs.aria': 'Alert state',
  'owner.alerts.evaluatedAt': 'Checked {when}',
  'owner.alerts.notEvaluated': 'The checks have not run yet',
  'owner.alerts.evaluate': 'Check now',
  'owner.alerts.evaluated': 'The checks have run.',

  'owner.alerts.state.attention': 'Needs attention',
  'owner.alerts.state.attention.hint': 'Open, and nobody has taken it or snoozed it yet.',
  'owner.alerts.state.acknowledged': 'Taken',
  'owner.alerts.state.acknowledged.hint':
    'Somebody on the team is dealing with it. If it gets worse, it goes back to “Needs attention”.',
  'owner.alerts.state.snoozed': 'Snoozed',
  'owner.alerts.state.snoozed.hint': 'Put off until a date, and back sooner if it gets worse.',
  'owner.alerts.state.resolved': 'Resolved',
  'owner.alerts.state.resolved.hint': 'The cause went away and the checks closed the alert by themselves.',

  'owner.alerts.source.health': 'Customer health',
  'owner.alerts.source.security': 'Security',
  'owner.alerts.source.mes': 'KaproMES',
  'owner.alerts.source.limits': 'Limits',
  'owner.alerts.source.scheduler': 'Schedule',
  'owner.alerts.source.queue': 'Queue',
  'owner.alerts.source.disk': 'Disk',

  'owner.alerts.filter.company': 'Customer',
  'owner.alerts.filter.allCompanies': 'All customers',
  'owner.alerts.filter.severity': 'Severity',
  'owner.alerts.filter.allSeverities': 'All severities',
  'owner.alerts.filter.source': 'Source',
  'owner.alerts.filter.allSources': 'All sources',

  'owner.alerts.platform': 'Platform',
  'owner.alerts.openedAt': 'opened {when}',
  'owner.alerts.lastSeen': 'Last check that found it: {when}',
  'owner.alerts.acknowledgedBy': 'Taken: {name}, {when}',
  'owner.alerts.acknowledgedAnon': 'Taken {when}',
  'owner.alerts.snoozedBy': 'Snoozed until {until} — {name}',
  'owner.alerts.snoozedAnon': 'Snoozed until {until}',
  'owner.alerts.resolvedAt': 'resolved {when} · lasted {duration}',
  'owner.alerts.resolvedAtShort': 'resolved {when}',
  'owner.alerts.open.generic': 'Open',
  'owner.alerts.open.aria': '{target} — {title}',
  'owner.alerts.actions.aria': 'Actions for the alert “{title}”',

  'owner.alerts.action.acknowledge': 'I’ll take it',
  'owner.alerts.action.unacknowledge': 'Hand it back',
  'owner.alerts.action.snooze': 'Snooze',
  'owner.alerts.action.snooze.days.one': '{count} day',
  'owner.alerts.action.snooze.days.few': '{count} days',
  'owner.alerts.action.snooze.days.many': '{count} days',
  'owner.alerts.action.snooze.date': 'Until a date…',
  'owner.alerts.action.unsnooze': 'End the snooze',
  'owner.alerts.action.mute': 'Mute',

  'owner.alerts.done.acknowledged': 'Taken. The team can see you are on it.',
  'owner.alerts.done.unacknowledged': 'Handed back.',
  'owner.alerts.done.snoozed': 'Snoozed until {until}.',
  'owner.alerts.done.unsnoozed': 'The snooze has ended.',
  'owner.alerts.done.muted': 'Muted. The alert closes at the latest on the next check.',
  'owner.alerts.done.unmuted': 'Unmuted. If the cause is still there, the alert comes back on the next check.',

  'owner.alerts.snooze.title': 'Snooze until a date',
  'owner.alerts.snooze.description':
    'The alert goes back to “Needs attention” at the start of that day — or sooner, if it gets worse.',
  'owner.alerts.snooze.date': 'Date',
  'owner.alerts.snooze.date.hint': 'From tomorrow to {max}, at most 30 days.',
  'owner.alerts.snooze.confirm': 'Snooze',

  'owner.alerts.mute.title': 'Mute “{title}”?',
  'owner.alerts.mute.body':
    'A muted alert closes and does not open again until it is unmuted. The check keeps running — it just stops telling anybody, here or by e-mail.',
  'owner.alerts.mute.scope': 'For whom',
  'owner.alerts.mute.scope.company': 'Only for {company}',
  'owner.alerts.mute.scope.everywhere': 'For every customer',
  'owner.alerts.mute.scope.platform': 'This alert is about the whole platform, so it is muted everywhere.',
  'owner.alerts.mute.reason': 'Reason (optional)',
  'owner.alerts.mute.reason.hint': 'The rest of the team sees it in the list of muted alerts.',
  'owner.alerts.mute.reason.placeholder': 'e.g. The customer is deliberately not using 5S until spring.',
  'owner.alerts.mute.confirm': 'Mute',

  'owner.alerts.mutes.button': 'Muted ({count})',
  'owner.alerts.mutes.title': 'Muted alerts',
  'owner.alerts.mutes.description':
    'Alert types that are not shown, for one customer or for all. When you unmute one, it comes back on the next check if the cause is still there.',
  'owner.alerts.mutes.loading': 'Loading muted alerts…',
  'owner.alerts.mutes.empty': 'Nothing is muted — everything the checks find is shown.',
  'owner.alerts.mutes.everywhere': 'All customers',
  'owner.alerts.mutes.created': 'muted {when}',
  'owner.alerts.mutes.createdBy': 'muted {when} — {name}',
  'owner.alerts.mutes.reason': '“{reason}”',
  'owner.alerts.mutes.unmute': 'Unmute',
  'owner.alerts.mutes.unmute.aria': 'Unmute: {type} — {scope}',

  'owner.alerts.empty.attention.title': 'Nothing waiting',
  'owner.alerts.empty.attention': 'The last check, {when}, found nothing waiting for somebody to take it.',
  'owner.alerts.empty.never':
    'The checks have not run yet. They run every ten minutes — if this stays, look at the schedule on the System page.',
  'owner.alerts.empty.acknowledged': 'Nobody has taken anything at the moment.',
  'owner.alerts.empty.snoozed': 'Nothing is snoozed.',
  'owner.alerts.empty.resolved': 'No alert has been resolved yet.',
  'owner.alerts.empty.filtered': 'No alert matches the filters.',
  'owner.alerts.pager.empty': 'No alerts',

  'owner.alerts.company.title': 'Alerts',
  'owner.alerts.company.description': 'This customer’s open alerts, from every check — not only the health score.',
  'owner.alerts.company.empty': 'This customer has no open alerts.',

  'owner.alerts.badge.one': '{count} alert needs attention',
  'owner.alerts.badge.few': '{count} alerts need attention',
  'owner.alerts.badge.many': '{count} alerts need attention',
  'owner.alerts.badge.critical': '{attention}, {critical} of them urgent',

  'owner.alerts.type.health.dormant': 'The customer has gone quiet',
  'owner.alerts.type.health.activityDrop': 'Activity is falling',
  'owner.alerts.type.health.lowReach': 'Few people use the application',
  'owner.alerts.type.health.neverActivated': 'Accounts never used',
  'owner.alerts.type.health.backlogOverdue': 'Reports past their due date',
  'owner.alerts.type.health.fiveSCoverageLow': 'Low 5S coverage',
  'owner.alerts.type.health.seatsNearlyFull': 'Seats nearly full',
  'owner.alerts.type.health.storageNearlyFull': 'Storage nearly full',
  'owner.alerts.type.health.mesSyncStale': 'The MES sync has stopped',
  'owner.alerts.type.health.adminWithoutTwoFactor': 'Administrator without two-factor protection',
  'owner.alerts.type.health.unknown': 'Customer health: {key}',
  'owner.alerts.type.security.signInAfterFailures': 'Possibly a guessed password',
  'owner.alerts.type.security.repeatedFailures': 'Repeated failed sign-ins',
  'owner.alerts.type.security.ownerSignInFailures': 'Failed sign-ins to owner accounts',
  'owner.alerts.type.security.scanningAddress': 'One address trying many accounts',
  'owner.alerts.type.security.unknownCodeAttempts': 'Attempts with unknown codes',
  'owner.alerts.type.mes.syncFailing': 'The MES sync is failing',
  'owner.alerts.type.mes.deliveriesFailing': 'Deliveries to the MES are failing',
  'owner.alerts.type.limits.storageFillsSoon': 'The storage quota is filling up',
  'owner.alerts.type.limits.seatsFillSoon': 'Seats are filling up',
  'owner.alerts.type.scheduler.late': 'A scheduled job is late',
  'owner.alerts.type.scheduler.failing': 'A scheduled job is failing',
  'owner.alerts.type.queue.failedJobs': 'Failed jobs in the queue',
  'owner.alerts.type.disk.fillsSoon': 'The disk is filling up',
  'owner.alerts.type.unknown': 'Alert: {type}',

  'owner.alerts.fact.signInAfterFailures':
    'Account {code}: {failures}, then a successful sign-in {when}, from {ip}.',
  'owner.alerts.fact.signInAfterFailures.noIp': 'Account {code}: {failures}, then a successful sign-in {when}.',
  'owner.alerts.fact.repeatedFailures': 'Account {code}: {failures} in the last 24 hours, the last {when}.',
  'owner.alerts.fact.ownerSignInFailures':
    'Owner accounts in the last 24 hours — failed attempts: {failed}, throttled: {throttled}. The last {when}.',
  'owner.alerts.fact.scanningAddress':
    'Address {ip} in the last 24 hours — failed attempts: {failures}, different accounts: {accounts}. The last {when}.',
  'owner.alerts.fact.unknownCodeAttempts':
    'Failed attempts with an unknown code or badge in the last 24 hours: {failed}. The last {when}.',
  'owner.alerts.fact.mesSyncFailing': '{runs} Last successful {when}.',
  'owner.alerts.fact.mesSyncFailing.never': '{runs} None has succeeded yet.',
  'owner.alerts.fact.mesDeliveriesFailing': 'Failed deliveries in the last 24 hours: {failed}. The last {when}.',
  'owner.alerts.fact.storageUsed': '{used} of {quota} in use.',
  'owner.alerts.fact.seatsUsed': '{used} of {limit} seats in use.',
  'owner.alerts.fact.schedulerLate': 'The job “{task}” was due to start {when} and has not run.',
  'owner.alerts.fact.schedulerFailing': 'The job “{task}” failed {when}.',
  'owner.alerts.fact.queueFailedJobs': 'Failed jobs in the last 24 hours: {count}. The last {when}.',
  'owner.alerts.fact.diskFree': '{free} free of {total}.',
  'owner.alerts.fact.diskDays': 'At this rate it is full in {days}.',
  'owner.alerts.fact.diskFull': 'The disk is full.',
  'owner.alerts.fact.unknown': 'This kind of alert is newer than this version of the application.',

  // ---- Team: my own alert e-mails ---------------------------------------
  'owner.alertPrefs.title': 'My alert e-mails',
  'owner.alertPrefs.description': 'Only for you — every team member chooses for themselves.',
  'owner.alertPrefs.loading': 'Loading settings…',
  'owner.alertPrefs.immediate': 'Urgent alerts at once',
  'owner.alertPrefs.immediate.description':
    'An e-mail the moment an urgent alert opens that nobody has taken or snoozed — one per check, however many there are.',
  'owner.alertPrefs.digest': 'Morning summary',
  'owner.alertPrefs.digest.description':
    'Every morning at 7:30: what is open, what is taken or snoozed, and what opened and closed in the last 24 hours. Not sent when there is nothing to say.',
  'owner.alertPrefs.email': 'Sent to {email}.',
  'owner.alertPrefs.noEmail':
    'Your account has no e-mail address, so these alerts cannot reach you. Add one on the My account page.',
  'owner.alertPrefs.saved': 'Saved.',

  // ---- The customer's main administrator ----------------------------
  'owner.superAdmin.title': 'Main administrator',
  'owner.superAdmin.description':
    'One per company: changes the settings, the integration, plants and administrators, and sees every plant. Until one is chosen, every administrator of the company has full rights.',
  'owner.superAdmin.current': 'The main administrator is {name}.',
  'owner.superAdmin.compat': 'None chosen — every administrator has full rights and sees every plant.',
  'owner.superAdmin.badge': 'Main administrator',
  'owner.superAdmin.choose': 'Choose',
  'owner.superAdmin.choose.aria': 'Make {name} the main administrator',
  'owner.superAdmin.access.all': 'All plants',
  'owner.superAdmin.access.none': 'No plant',
  'owner.superAdmin.access.none.explain':
    'Once the company has a main administrator, this administrator sees nothing until they are given a plant.',
  'owner.superAdmin.warning.one':
    '{count} administrator has no plant and will see nothing once you choose a main administrator: {names}.',
  'owner.superAdmin.warning.few':
    '{count} administrators have no plant and will see nothing once you choose a main administrator: {names}.',
  'owner.superAdmin.warning.many':
    '{count} administrators have no plant and will see nothing once you choose a main administrator: {names}.',
  'owner.superAdmin.empty': 'The company has no active administrator.',
  'owner.superAdmin.confirm': 'Make "{name}" the main administrator?',
  'owner.superAdmin.confirm.body':
    'From then on only {name} changes the settings of {company}, the KaproMES integration, plants and administrators. The other administrators see only their own plants.',
  'owner.superAdmin.confirm.action': 'Choose',
  'owner.superAdmin.saved': 'The main administrator has been set.',
  'owner.superAdmin.reset': 'Return to all administrators',
  'owner.superAdmin.reset.confirm': 'Give every administrator full rights again?',
  'owner.superAdmin.reset.body':
    '{name} is no longer the main administrator, and every administrator of {company} has full rights and sees every plant again.',
  'owner.superAdmin.reset.done': 'Every administrator has full rights again.',
}
